/**
 * The board, as the board screens see it.
 *
 * These mirror hq_task_boards / hq_task_columns / hq_tasks plus the columns
 * hq/sql/2027-02-03_team_board.sql added (short numbers, column kinds,
 * labels, history). Screens get them from a BoardRepo (./repo.ts) and never
 * from supabase-js directly.
 */

export type ColumnKind =
  | "draft" | "ready" | "wip" | "waiting" | "ready_for_testing" | "testing" | "complete";

export type Priority = "low" | "med" | "high";

export interface BoardRow {
  id: string;
  name: string;
  description: string | null;
  owner_employee_id: string | null;
  team_id: string | null;
  requires_priority_link?: boolean;
  /** "SS" makes cards read SS-12. Null on boards that never set one. */
  key_prefix: string | null;
  standup_days: number[];
  standup_time: string;
  standup_tz: string;
  /**
   * Every active employee can open this board. Absent until
   * sql/2027-02-18_board_open_to_all_staff.sql is applied, which means closed.
   */
  open_to_all_staff?: boolean;
}

export interface ColumnRow {
  id: string;
  board_id: string;
  name: string;
  position: number;
  is_done: boolean;
  /** Stable role, so stand-up and bot rules survive a rename. */
  kind: ColumnKind | null;
}

export interface Card {
  id: string;
  board_id: string;
  column_id: string;
  number: number;
  title: string;
  description: string | null;
  position: number;
  priority: Priority;
  due_date: string | null;
  pe_objective_id?: string | null;
  created_by: string | null;
  /** Set when an AI teammate created the card through the Tool Gateway. */
  created_by_agent_id: string | null;
  completed_at: string | null;
  moved_at: string | null;
  /**
   * When set, the card is off the board. The row stays, and ?card= still
   * opens it. Null on boards whose archive migration has not been applied.
   */
  archived_at?: string | null;
  /** Set when a person archived it. Null when the Sunday job did, or when it is on the board. */
  archived_by?: string | null;
  /** deleted = a person pressed Delete. auto_complete = the Sunday job. */
  archive_reason?: "deleted" | "auto_complete" | null;
  created_at: string;
  updated_at: string;
}

export interface Person {
  id: string;
  first_name: string | null;
  last_name: string | null;
  preferred_name: string | null;
  email: string | null;
  role: string | null;
  employee_kind: "human" | "ai" | "service" | null;
}

export interface Label {
  id: string;
  board_id: string;
  name: string;
  color: LabelColor;
  position: number;
}

export type LabelColor = "gray" | "teal" | "green" | "amber" | "red" | "blue" | "purple" | "pink";

export interface AssigneeLink { task_id: string; employee_id: string; }
export interface LabelLink { task_id: string; label_id: string; }

/** Everything one board screen needs, loaded together. */
export interface BoardSnapshot {
  board: BoardRow;
  columns: ColumnRow[];
  cards: Card[];
  assignees: AssigneeLink[];
  labels: Label[];
  labelLinks: LabelLink[];
  /** Explicit members, the owner, and the board team's members. An open board still lists these; the picker uses people. */
  memberIds: string[];
  /** Active humans and AI teammates, for names, pickers and creators. */
  people: Person[];
}

/** "SS-12", or "#12" on a board with no prefix. */
export function shortId(board: Pick<BoardRow, "key_prefix">, card: Pick<Card, "number">): string {
  return board.key_prefix ? `${board.key_prefix}-${card.number}` : `#${card.number}`;
}

/** Opens the card, including one that has been archived off the board. */
export function boardCardHref(
  boardId: string,
  card: { id: string; number?: number | null },
  keyPrefix: string | null | undefined,
): string {
  const ref = keyPrefix && card.number != null ? `${keyPrefix}-${card.number}` : card.id;
  return `/tasks/${boardId}?card=${encodeURIComponent(ref)}`;
}

export function displayName(p: Pick<Person, "first_name" | "last_name" | "preferred_name" | "email">): string {
  const first = p.preferred_name?.trim() || p.first_name?.trim() || "";
  const name = [first, p.last_name?.trim()].filter(Boolean).join(" ");
  return name || p.email || "Unknown";
}

export function personInitials(p: Pick<Person, "first_name" | "last_name" | "preferred_name" | "email">): string {
  const f = (p.preferred_name || p.first_name || "").trim()[0];
  const l = (p.last_name || "").trim()[0];
  if (f || l) return ((f || "") + (l || "")).toUpperCase();
  return (p.email || "?").slice(0, 2).toUpperCase();
}
